import { myWalletQueryOptions } from "@/api/me";
import { conversationMessagesQueryOptions, conversationsQuery } from "@/api/messages";
import { transactionsQueryKey } from "@/api/transactions";
import { auth, queryClient } from "@/api/utils";
import { config } from "@common/utils/config";
import type {
  ConversationUpdated,
  WalletUpdated,
} from "@givenwell/sockets-api/Givenwell.Sockets.Abstractions.Client.Models";
import type { Disposable } from "@givenwell/sockets-api/TypedSignalR.Client";
import { getHubProxyFactory, getReceiverRegister } from "@givenwell/sockets-api/TypedSignalR.Client";
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from "@microsoft/signalr";

const HUB_PATH = "/hubs/givenwell";

let connection: HubConnection | null = null;
let conversationsDisposable: Disposable | null = null;
let walletDisposable: Disposable | null = null;
let isConnecting = false;

function getConnection(): HubConnection {
  if (!connection) {
    connection = new HubConnectionBuilder()
      .withUrl(`${config.socketsUrl}${HUB_PATH}`, {
        accessTokenFactory: () => auth.getAccessToken(),
      })
      .withAutomaticReconnect()
      .configureLogging(config.environmentName === "dev" ? LogLevel.Information : LogLevel.Warning)
      .build();

    connection.onclose(error => {
      console.log("[Sockets] Connection closed", error);
    });

    connection.onreconnecting(error => {
      console.log("[Sockets] Reconnecting...", error);
    });

    connection.onreconnected(connectionId => {
      console.log("[Sockets] Reconnected", connectionId);
    });
  }
  return connection;
}

export async function connectSockets(): Promise<void> {
  if (isConnecting) {
    return;
  }

  const conn = getConnection();

  if (conn.state === HubConnectionState.Connected) {
    return;
  }

  if (conn.state === HubConnectionState.Connecting || conn.state === HubConnectionState.Reconnecting) {
    return;
  }

  isConnecting = true;

  try {
    await conn.start();
    console.log("[Sockets] Connected");

    // Register the receiver for conversation updates
    const conversationsRegister = getReceiverRegister("IConversationsReceiver");
    conversationsDisposable = conversationsRegister.register(conn, {
      async onConversationUpdated(message: ConversationUpdated) {
        console.log("[Sockets] Conversation updated:", message.conversationId, message.messageId);

        // Invalidate the conversations list (for unread badges, etc.)
        queryClient.invalidateQueries(conversationsQuery);

        // Invalidate the specific conversation's messages
        queryClient.invalidateQueries(conversationMessagesQueryOptions(message.conversationId));
      },
    });

    // Register the receiver for wallet balance updates
    const walletRegister = getReceiverRegister("IWalletReceiver");
    walletDisposable = walletRegister.register(conn, {
      async onWalletUpdated(_message: WalletUpdated) {
        queryClient.invalidateQueries(myWalletQueryOptions);
        queryClient.invalidateQueries({ queryKey: transactionsQueryKey });
      },
    });

    // Get the hub proxy (for sending messages if needed in the future)
    const _hubProxy = getHubProxyFactory("IGivenwellSender").createHubProxy(conn);
  } catch (error) {
    console.error("[Sockets] Failed to connect:", error);
  } finally {
    isConnecting = false;
  }
}

export async function disconnectSockets(): Promise<void> {
  if (conversationsDisposable) {
    conversationsDisposable.dispose();
    conversationsDisposable = null;
  }

  if (walletDisposable) {
    walletDisposable.dispose();
    walletDisposable = null;
  }

  if (connection && connection.state !== HubConnectionState.Disconnected) {
    try {
      await connection.stop();
      console.log("[Sockets] Disconnected");
    } catch (error) {
      console.error("[Sockets] Failed to disconnect:", error);
    }
  }

  connection = null;
}

export function getSocketConnection(): HubConnection | null {
  return connection;
}
